<script setup lang="ts">
import type { ChartData, ChartOptions } from 'chart.js'
import type { PropType } from 'vue'
import { Bar } from 'vue-chartjs'
import merge from 'lodash/merge'
import cloneDeep from 'lodash/cloneDeep'

type BarOptions = ChartOptions<'bar'>

const props = defineProps({
  defaultOptions: {
    type: Object as PropType<BarOptions>,
    required: true,
  },
  visualizerOptions: {
    type: Object,
    required: true,
  },
  chartClasses: {
    type: String,
    required: true,
  },
  legendClasses: {
    type: String,
    default: '',
  },
  data: {
    type: Object as PropType<ChartData<'bar'>>,
    required: true,
  },
})

const emit = defineEmits(['chartRef'])

const chartRef = ref()
const options = merge(cloneDeep(props.defaultOptions), {
  indexAxis: 'y',

  scales: {
    y: {
      ticks: {
        callback: function (_val, index) {
          return formatText(props.data.labels[index])
        },
      },
    },

    x: {
      ticks: {
        callback(value) {
          return formatText(value)
        },
      },
    },
  },

  plugins: {
    tooltip: {
      mode: 'point',
      intersect: true,
    },
  },
} as BarOptions)

const onClickLegendItem = useOnClickLegendItem(chartRef)
</script>

<template>
  <div>
    <div class="relative">
      <Bar
        ref="chartRef"
        :class="chartClasses"
        :data="data"
        :options="options"
        @vue:mounted="emit('chartRef', chartRef)"
      />
    </div>
    <ChartLegend
      v-if="props.visualizerOptions.legend.position !== 'none'"
      :class="legendClasses"
      :datasets="data.datasets"
      @click-legend-item="onClickLegendItem"
    />
  </div>
</template>
